import { EventType } from "@/analytics/analytics-events";
import { TimeseriesDropdownStyleProperty } from "@/analytics/generic-events-properties";
import {
  ElementIcon,
  ElementIconType,
  iconForElement,
  LocationTypes,
} from "@/components/ui/icons";
import { PhotoGallery } from "@/modes/walk-mode/photo-preview-list";
import {
  isElementPhotosGroup,
  TimestampElement,
} from "@/modes/walk-mode/walk-overlay-selectors";
import { selectIsVideoModeTimePoint } from "@/modes/walk-mode/walk-scene-selectors";
import { useAppSelector } from "@/store/store-hooks";
import { useLocalStorage } from "@faro-lotv/app-component-toolbox";
import {
  ChipCounter,
  cyan,
  Dropdown,
  DropdownOption,
  FaroIconButton,
  Img360SmallIcon,
  ListViewIcon,
  grey,
  NoTranslate,
  ScanIcon,
  TilesViewIcon,
  TruncatedFaroText,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  GUID,
  IElement,
  IElementBase,
  IElementType,
  isIElementGenericDataset,
  isIElementOdometryPathSection,
  isIElementRoomSection,
  isIElementSectionDataSession,
  isIElementVideoRecording,
} from "@faro-lotv/ielement-types";
import {
  newestToOldest,
  selectIElement,
  selectIElementChildren,
} from "@faro-lotv/project-source";
import { SelectChangeEvent, Stack, SxProps, Theme } from "@mui/material";
import { isEqual } from "es-toolkit";
import { useCallback, useMemo, useState, type JSX } from "react";
import { shallowEqual } from "react-redux";
import { selectModeComputedOptions } from "./timestamp-dropdown-selectors";

// Sort the dropdown options to have datasets/point clouds first, others(like 360s or photos groups) second, location markers last
function sortDropdownOptions(options: TimestampElement[]): TimestampElement[] {
  // Separate datasets/point clouds from location markers (rooms)
  const datasets: IElement[] = [];
  const locationMarkers: IElement[] = [];
  const others: TimestampElement[] = [];

  options.forEach((option) => {
    if (isElementPhotosGroup(option)) {
      others.push(option);
    } else if (isIElementRoomSection(option) && !option.childrenIds?.length) {
      // Location markers: rooms without children
      locationMarkers.push(option);
    } else if (
      isIElementSectionDataSession(option) ||
      isIElementGenericDataset(option)
    ) {
      // Datasets and point clouds
      datasets.push(option);
    } else {
      others.push(option);
    }
  });

  // Sort datasets alphabetically by name
  datasets.sort((a, b) => a.name.localeCompare(b.name));

  // Sort others by date (newest to oldest)
  others.sort(newestToOldest);

  // Datasets first, then others, location markers at the end
  return [...datasets, ...others, ...locationMarkers];
}

type TimestampDropdownProps = {
  /** ID of the selected option */
  value: GUID;

  /** List of options */
  options: TimestampElement[];

  /** The photo gallery for the options */
  photoGallery?: PhotoGallery;

  /** Custom function to format the label as required */
  labelFormatter(iElement: TimestampElement): JSX.Element | string;

  /** List of id of the elements to disable in the drop-down menu */
  disabledElements?: GUID[];

  /** Callback to signal a new option was selected */
  onChange(ev: SelectChangeEvent<GUID>): void;

  /** Custom styles for the container of the dropdown */
  containerSx?: SxProps<Theme>;

  /** Custom styles for the dropdown */
  sx?: SxProps<Theme>;
};

/**
 * @returns a dropdown component for timestamp selections
 */
export function TimestampDropdown({
  value,
  options,
  photoGallery,
  labelFormatter,
  disabledElements = [],
  onChange: onChangeOption,
  sx,
  containerSx,
}: TimestampDropdownProps): JSX.Element {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [showPhotoGallery, setShowPhotoGallery] = useLocalStorage(
    "showPhotoGalleryView",
    false,
  );

  // Sort the options from newest to oldest
  const sortedOptions = useMemo(() => sortDropdownOptions(options), [options]);

  const onClose = useCallback(() => {
    setIsDropdownOpen(false);
  }, [setIsDropdownOpen]);

  const onOpen = useCallback(() => {
    setIsDropdownOpen(true);
  }, [setIsDropdownOpen]);

  const onChange = useCallback(
    (ev: SelectChangeEvent<GUID>) => {
      onChangeOption(ev);
      setIsDropdownOpen(false);
    },
    [onChangeOption],
  );

  const onSwitchGalleryView = useCallback(
    (showGallery: boolean) => {
      if (showPhotoGallery !== showGallery) {
        Analytics.track<TimeseriesDropdownStyleProperty>(
          EventType.toggleTimeseriesDropdownStyle,
          { style: showGallery ? "gallery" : "text list" },
        );
        setShowPhotoGallery(showGallery);
      }
    },
    [setShowPhotoGallery, showPhotoGallery],
  );

  // Compute the default mode for each value in the dropdown
  const modeComputedOptions: ReturnType<typeof selectModeComputedOptions> =
    useAppSelector(
      selectModeComputedOptions(sortedOptions),
      (oldValue, newValue) => isEqual(oldValue, newValue),
    );

  const dropdownOptions = useMemo(() => {
    const dropdownOptions: DropdownOption[] = modeComputedOptions.map(
      (option) => ({
        key: option.id,
        value: option.id,
        label: (
          <TimestampDropdownOptionLabel
            element={option}
            labelFormatter={labelFormatter}
          />
        ),
        secondaryText:
          showPhotoGallery && photoGallery
            ? photoGallery.gallery[option.id]
            : undefined,
        isDisabled: !option.mode || disabledElements.includes(option.id),
        isFilteredOut: option.isFilteredOut,
      }),
    );

    // add header with view type buttons if photo gallery is available
    if (photoGallery) {
      dropdownOptions.unshift({
        isHeader: true,
        key: "",
        value: "",
        label: (
          <Stack
            direction="row"
            sx={{ justifyContent: "space-between", alignItems: "center" }}
          >
            <TruncatedFaroText
              variant="bodyS"
              sx={{
                textWrap: "nowrap",
                color: "currentcolor",
              }}
            >
              <NoTranslate>{photoGallery.location}</NoTranslate>
            </TruncatedFaroText>
            <Stack direction="row">
              <FaroIconButton
                size="s"
                color={showPhotoGallery ? grey[100] : cyan[400]}
                onClick={() => onSwitchGalleryView(false)}
              >
                <ListViewIcon />
              </FaroIconButton>
              <FaroIconButton
                size="s"
                color={showPhotoGallery ? cyan[400] : grey[100]}
                onClick={() => onSwitchGalleryView(true)}
              >
                <TilesViewIcon />
              </FaroIconButton>
            </Stack>
          </Stack>
        ),
      });
    }

    return dropdownOptions;
  }, [
    disabledElements,
    labelFormatter,
    modeComputedOptions,
    onSwitchGalleryView,
    photoGallery,
    showPhotoGallery,
  ]);

  if (options.length === 1) {
    return (
      <TimestampDropdownOptionLabel
        element={options[0]}
        labelFormatter={labelFormatter}
        sx={{ px: 0.25, py: 0.5, ...sx }}
      />
    );
  }

  return (
    <Dropdown
      dark
      value={value}
      onClose={onClose}
      onOpen={onOpen}
      variant="underlined"
      onChange={onChange}
      options={dropdownOptions}
      containerSx={containerSx}
      sx={{
        ".MuiSelect-outlined.MuiOutlinedInput-input": {
          fontSize: "1em",
          p: 1.5,
          pr: 4,
        },
        // Don't show line under the selected option, in the idle state
        ...(isDropdownOpen ? {} : { boxShadow: "none" }),
        ...sx,
      }}
    />
  );
}

type TimestampDropdownOptionLabelProps = {
  /** an IElement whose label needs to be shown in the dropdown */
  element: TimestampElement;

  /** a formatter function which lets the label to be customised */
  labelFormatter(element: TimestampElement): JSX.Element | string;

  /** Custom styles for the label */
  sx?: SxProps<Theme>;
};

/**
 * @returns a label with the element icon, name and timestamp
 */
function TimestampDropdownOptionLabel({
  element,
  labelFormatter,
  sx,
}: TimestampDropdownOptionLabelProps): JSX.Element {
  return (
    <Stack
      component="span"
      direction="row"
      sx={[
        { alignItems: "center", gap: 1 },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {isElementPhotosGroup(element) ? (
        <ElementIcon icon={ElementIconType.ImageIcon} sx={ICON_LABEL_SX} />
      ) : (
        <IconLabelForElement iElement={element} />
      )}
      <TruncatedFaroText
        variant="bodyM"
        sx={{
          textWrap: "nowrap",
          color: "currentcolor",
        }}
      >
        {labelFormatter(element)}
      </TruncatedFaroText>
      {isElementPhotosGroup(element) && element.photos.length > 1 && (
        <ChipCounter count={element.photos.length} />
      )}
    </Stack>
  );
}

/** Style to apply to the icons in the dropdown */
const ICON_LABEL_SX: SxProps<Theme> = {
  width: "18px",
  height: "18px",
  lineHeight: 0,
};

type IconLabelForElementProps = {
  iElement: IElement;
};

function IconLabelForElement({
  iElement,
}: IconLabelForElementProps): JSX.Element {
  const parent = useAppSelector(selectIElement(iElement.parentId));

  const elementChildren = useAppSelector(
    selectIElementChildren(iElement.id),
    shallowEqual,
  );

  const childrenTypeHints = useMemo(
    () => elementChildren.map((el: IElementBase) => el.typeHint),
    [elementChildren],
  );

  const isVideoModeTimePoint = useAppSelector(
    selectIsVideoModeTimePoint(iElement),
  );

  // Specific icon to display for the current node
  const icon = useMemo(
    () =>
      iconForElement(
        iElement.type,
        iElement.typeHint,
        childrenTypeHints,
        isVideoModeTimePoint ? parent?.type : undefined,
        isVideoModeTimePoint ? parent?.typeHint : undefined,
        isVideoModeTimePoint ? LocationTypes.VideoMode : undefined,
      ),
    [
      iElement.type,
      iElement.typeHint,
      childrenTypeHints,
      parent?.type,
      parent?.typeHint,
      isVideoModeTimePoint,
    ],
  );

  // Use specific icon for the scans
  if (iElement.type === IElementType.img360) {
    // Use pano icon for panos from paths
    if (
      !!parent &&
      (isIElementVideoRecording(parent) ||
        isIElementOdometryPathSection(parent))
    ) {
      return <Img360SmallIcon sx={ICON_LABEL_SX} />;
    }

    // Otherwise use the scan icon
    return <ScanIcon sx={ICON_LABEL_SX} />;
  }

  return <ElementIcon icon={icon} sx={ICON_LABEL_SX} />;
}
